import {observer} from "mobx-react";
import React, {useState} from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack
} from "@mui/material";
import {FieldState, FormState} from "formstate";
import {LoadingButton} from "@mui/lab";
import {useInstance} from "react-ioc";
import {ApiClients} from "app/modules/company/services/AxiosBaseClient";
import {required} from "app/modules/common/form/validators";
import {SectionSelectField, SectionTextField} from "app/modules/common/details/text-field";
import {MainStore} from "app/modules/company/stores/MainStore";
import {
    OrderStopAddressModel,
    OrderStopType
} from "app/modules/company/api/clients.api";
import {ApiErrorHandler} from "app/modules/common/static/ErrrorHandler";

export interface DialogAddressNewProps {
    closeModal: () => void;
    setAddress: (addressId: number, fullAddress: string, addressName: string) => void;
    title: string;
    orderStopType: OrderStopType;
}

export const DialogAddressNew = observer((props: DialogAddressNewProps) => {
    const [formState, _] = useState(new FormState({
        name: new FieldState(null).validators(required),
        address: new FieldState("").validators(required),
        address2: new FieldState(""),
        city: new FieldState("").validators(required),
        state: new FieldState("").validators(required),
        zip: new FieldState(null).validators(required),
        country: new FieldState(""),
        note: new FieldState("")
    }));

    const [isLoading, setLoading] = useState(false);

    const store = useInstance(ApiClients);
    const mainStore = useInstance(MainStore);

    const save = async () => {
        if(isLoading) {
            return;
        }

        await formState.validate();

        if(formState.hasError) {
            return;
        }

        try {
            setLoading(true);
            const model = await store.orderClient.upsertOrderStopAddresses(new OrderStopAddressModel({
                name: formState.$.name.value,
                address: formState.$.address.value,
                address2: formState.$.address2.value,
                city: formState.$.city.value,
                state: formState.$.state.value,
                zIP: formState.$.zip.value,
                country: formState.$.country.value,
                note: formState.$.note.value,
                type: props.orderStopType
            }));

            props.setAddress(model.id, model.fullAddress, model.name);
        }
        catch (ex) { ApiErrorHandler(ex); }
        finally {
            setLoading(false);
        }
    };

    return <Dialog
        open={true}
        onClose={props.closeModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
    >
        <DialogTitle id="alert-dialog-title">
            {props.title}
        </DialogTitle>
        <DialogContent
            sx={{ backgroundColor: "#f0f0f0" }}
        >
            <DialogContent id="alert-dialog-description" style={{ minWidth: "400px" }}>
                <Stack spacing={2} direction={"column"}>
                    <SectionTextField
                        label={"Name"}
                        field={formState.$.name}
                        fullWidth
                    />
                    <SectionTextField
                        label={"Address"}
                        field={formState.$.address}
                        fullWidth
                    />
                    <SectionTextField
                        label={"Address 2"}
                        field={formState.$.address2}
                        fullWidth
                    />
                    <Stack spacing={2} direction={"row"}>
                        <SectionTextField
                            label={"City"}
                            field={formState.$.city}
                            width={"33%"}
                        />
                        <SectionSelectField
                            label={"State"}
                            field={formState.$.state}
                            width={"33%"}
                            items={() => mainStore.tenantData.states.map((x) => <MenuItem value={x.code} key={x.code}>{x.name}</MenuItem>)}
                        />
                        <SectionTextField
                            label={"ZIP"}
                            field={formState.$.zip}
                            width={"33%"}
                        />
                    </Stack>
                    <SectionTextField
                        label={"Notes"}
                        field={formState.$.note}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
        </DialogContent>
        <DialogActions>
            <LoadingButton loading={isLoading} onClick={props.closeModal}>Close</LoadingButton>
            <LoadingButton loading={isLoading} variant="outlined" onClick={save} color="primary" autoFocus>
                Save
            </LoadingButton>
        </DialogActions>
    </Dialog>;
})
